import { useMutation, type UseMutationResult } from '@tanstack/react-query';

import { apiClient } from '@/api/client';
import type { PersonDto } from '@/features/patient-record/api';

import type { FhirPatientRequestBody } from './endpoints';

/**
 * Wrapper hook around `POST /fhir/patients`. The mutation input is just
 * the MRN — the caller supplies the current user id at construction
 * time so callers never have to plumb `useAuth().user.id` past this
 * boundary.
 *
 * All error handling is delegated to the caller (the "Import from
 * FHIR" dialog) which translates `error.status` to a user-facing i18n
 * message. Never surface `error.message` — it may include PHI from
 * downstream services.
 */
export function useImportFromFhir(
  currentUserId: string | null,
): UseMutationResult<PersonDto, Error, string> {
  return useMutation<PersonDto, Error, string>({
    mutationFn: (mrn) => {
      const body: FhirPatientRequestBody = {
        mrn,
        ...(currentUserId !== null ? { user: { id: currentUserId } } : {}),
      };
      return apiClient.importPatient(body);
    },
  });
}
